import React, { useState, useEffect, useCallback } from 'react';

// ============================================================================
// AKARI - Daily Light Up Puzzle
// ============================================================================
// A standalone React component for embedding on your website.
// 
// Usage: import Akari from './Akari';
//        <Akari />
//
// Features:
// - One puzzle per day, resets at midnight UTC
// - Same puzzle for all players globally
// - Procedurally generated (guaranteed solvable)
// - Progress saved to localStorage
// - Shareable results
// - Touch/mobile support
// ============================================================================

// Seeded random number generator (Mulberry32)
const mulberry32 = (seed) => {
  return () => {
    let t = seed += 0x6D2B79F5;
    t = Math.imul(t ^ t >>> 15, t | 1);
    t ^= t + Math.imul(t ^ t >>> 7, t | 61);
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
};

// Game configuration
const DIFFICULTIES = [
  { name: 'Easy', size: 5 },
  { name: 'Medium', size: 7 },
  { name: 'Hard', size: 9 },
];

const LAUNCH_DATE = new Date('2025-01-27T00:00:00Z'); // Set your launch date
const STORAGE_KEY_PREFIX = 'akari';

// Generate a valid Akari puzzle
const generatePuzzle = (size, seed) => {
  const random = mulberry32(seed);
  
  const grid = Array(size).fill(null).map(() => Array(size).fill(0));
  const bulbs = new Set();
  const walls = new Set();
  
  // Place some random walls (about 20-25% of cells)
  const wallCount = Math.floor(size * size * (0.2 + random() * 0.05));
  for (let i = 0; i < wallCount; i++) {
    const r = Math.floor(random() * size);
    const c = Math.floor(random() * size);
    walls.add(`${r},${c}`);
    grid[r][c] = -1;
  }
  
  const isWall = (r, c) => walls.has(`${r},${c}`);
  const hasBulb = (r, c) => bulbs.has(`${r},${c}`);
  
  const getLitCells = () => {
    const lit = new Set();
    bulbs.forEach(key => {
      const [br, bc] = key.split(',').map(Number);
      lit.add(key);
      const dirs = [[-1,0],[1,0],[0,-1],[0,1]];
      dirs.forEach(([dr, dc]) => {
        let r = br + dr, c = bc + dc;
        while (r >= 0 && r < size && c >= 0 && c < size && !isWall(r, c)) {
          lit.add(`${r},${c}`);
          r += dr;
          c += dc;
        }
      });
    });
    return lit;
  };
  
  const canPlaceBulb = (r, c) => {
    if (isWall(r, c) || hasBulb(r, c)) return false;
    const dirs = [[-1,0],[1,0],[0,-1],[0,1]];
    for (const [dr, dc] of dirs) {
      let nr = r + dr, nc = c + dc;
      while (nr >= 0 && nr < size && nc >= 0 && nc < size && !isWall(nr, nc)) {
        if (hasBulb(nr, nc)) return false;
        nr += dr;
        nc += dc;
      }
    }
    return true;
  };
  
  // Place bulbs until all cells are lit
  let attempts = 0;
  while (attempts < 1000) {
    const lit = getLitCells();
    const unlit = [];
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (!isWall(r, c) && !lit.has(`${r},${c}`)) {
          unlit.push([r, c]);
        }
      }
    }
    if (unlit.length === 0) break;
    
    const shuffled = unlit.sort(() => random() - 0.5);
    let placed = false;
    for (const [r, c] of shuffled) {
      if (canPlaceBulb(r, c)) {
        bulbs.add(`${r},${c}`);
        placed = true;
        break;
      }
    }
    if (!placed) {
      const wallList = Array.from(walls);
      if (wallList.length > 0) {
        const removeIdx = Math.floor(random() * wallList.length);
        const toRemove = wallList[removeIdx];
        walls.delete(toRemove);
        const [wr, wc] = toRemove.split(',').map(Number);
        grid[wr][wc] = 0;
      }
    }
    attempts++;
  }
  
  // Add number constraints to some walls
  walls.forEach(key => {
    const [r, c] = key.split(',').map(Number);
    const dirs = [[-1,0],[1,0],[0,-1],[0,1]];
    let adjBulbs = 0;
    dirs.forEach(([dr, dc]) => {
      const nr = r + dr, nc = c + dc;
      if (nr >= 0 && nr < size && nc >= 0 && nc < size && hasBulb(nr, nc)) {
        adjBulbs++;
      }
    });
    if (adjBulbs > 0 && random() < 0.6) {
      grid[r][c] = adjBulbs;
    }
  });
  
  return { grid, size, solution: bulbs };
};

// Date utilities
const getDayNumber = () => {
  const now = new Date();
  const utcNow = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const utcLaunch = Date.UTC(LAUNCH_DATE.getUTCFullYear(), LAUNCH_DATE.getUTCMonth(), LAUNCH_DATE.getUTCDate());
  return Math.floor((utcNow - utcLaunch) / (1000 * 60 * 60 * 24));
};

const getTodaysDifficulty = (dayNumber) => {
  const random = mulberry32(dayNumber * 7919);
  const roll = random();
  if (roll < 0.4) return 0;
  if (roll < 0.8) return 1;
  return 2;
};

const getFormattedDate = () => {
  const now = new Date();
  return now.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
};

const getTimeUntilNextPuzzle = () => {
  const now = new Date();
  const tomorrow = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
  const diff = tomorrow - now;
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);
  return `${hours}h ${minutes}m ${seconds}s`;
};

// Storage utilities
const getTodayKey = () => {
  const now = new Date();
  return `${STORAGE_KEY_PREFIX}-${now.getUTCFullYear()}-${now.getUTCMonth() + 1}-${now.getUTCDate()}`;
};

const saveProgress = (data) => {
  try {
    localStorage.setItem(getTodayKey(), JSON.stringify({ ...data, timestamp: Date.now() }));
  } catch {}
};

const getStoredProgress = () => {
  try {
    const data = localStorage.getItem(getTodayKey());
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
};

// Clipboard utility
const copyToClipboard = async (text, onSuccess, onError) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      onSuccess();
      return;
    }
  } catch {}
  
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    textarea.style.top = '-9999px';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    onSuccess();
  } catch {
    onError();
  }
};

// ============================================================================
// COMPONENT
// ============================================================================

const Akari = () => {
  const [puzzleData, setPuzzleData] = useState(null);
  const [bulbs, setBulbs] = useState(new Set());
  const [litCells, setLitCells] = useState(new Set());
  const [conflicts, setConflicts] = useState(new Set());
  const [gameOver, setGameOver] = useState(false);
  const [won, setWon] = useState(false);
  const [message, setMessage] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [displayTime, setDisplayTime] = useState('0:00');
  const [countdown, setCountdown] = useState('');
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [storedResult, setStoredResult] = useState(null);
  const gridRef = React.useRef(null);
  const containerRef = React.useRef(null);

  const dayNumber = getDayNumber();
  const todaysDifficulty = getTodaysDifficulty(dayNumber);
  const formattedDate = getFormattedDate();

  // Timer
  useEffect(() => {
    if (gameOver || !startTime) return;
    const interval = setInterval(() => {
      const seconds = Math.floor((Date.now() - startTime) / 1000);
      setDisplayTime(`${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`);
    }, 1000);
    return () => clearInterval(interval);
  }, [startTime, gameOver]);

  // Countdown
  useEffect(() => {
    const interval = setInterval(() => setCountdown(getTimeUntilNextPuzzle()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Send height to parent for iframe resizing
  useEffect(() => {
    const sendHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.scrollHeight;
        window.parent.postMessage({
          type: 'RESIZE_IFRAME',
          game: 'akari',
          height: height
        }, '*');
      }
    };
    
    sendHeight();
    const resizeObserver = new ResizeObserver(sendHeight);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    return () => resizeObserver.disconnect();
  }, [gameOver, won, bulbs]);

  // Initialize game
  const generateNewPuzzle = useCallback(() => {
    const diff = DIFFICULTIES[todaysDifficulty];
    const seed = dayNumber * 1000 + todaysDifficulty * 100;
    const puzzle = generatePuzzle(diff.size, seed);
    setPuzzleData({ ...puzzle, difficulty: diff.name });
    setBulbs(new Set());
    setLitCells(new Set());
    setConflicts(new Set());
    
    const stored = getStoredProgress();
    if (stored?.completed) {
      setAlreadyCompleted(true);
      setStoredResult(stored);
      setGameOver(true);
      setWon(true);
      setDisplayTime(stored.time);
      setMessage('Already completed today!');
    } else {
      setAlreadyCompleted(false);
      setStoredResult(null);
      setGameOver(false);
      setWon(false);
      setStartTime(Date.now());
      setDisplayTime('0:00');
      setMessage('Light up all cells!');
    }
  }, [dayNumber, todaysDifficulty]);

  useEffect(() => { generateNewPuzzle(); }, [generateNewPuzzle]);

  // Calculate lighting when bulbs change
  useEffect(() => {
    if (!puzzleData) return;
    
    const newLitCells = new Set();
    const newConflicts = new Set();
    const bulbPositions = Array.from(bulbs).map(key => {
      const [r, c] = key.split(',').map(Number);
      return { row: r, col: c };
    });

    const isBlackCell = (r, c) => puzzleData.grid[r][c] !== 0;

    bulbPositions.forEach(({ row, col }) => {
      newLitCells.add(`${row},${col}`);
      const directions = [[-1, 0], [1, 0], [0, -1], [0, 1]];
      directions.forEach(([dr, dc]) => {
        let r = row + dr;
        let c = col + dc;
        while (r >= 0 && r < puzzleData.size && c >= 0 && c < puzzleData.size && !isBlackCell(r, c)) {
          const key = `${r},${c}`;
          if (bulbs.has(key)) {
            newConflicts.add(`${row},${col}`);
            newConflicts.add(key);
          }
          newLitCells.add(key);
          r += dr;
          c += dc;
        }
      });
    });

    setLitCells(newLitCells);
    setConflicts(newConflicts);

    // Check win condition
    if (gameOver) return;
    if (newConflicts.size > 0) return;

    // Check all white cells are lit
    let allLit = true;
    for (let r = 0; r < puzzleData.size; r++) {
      for (let c = 0; c < puzzleData.size; c++) {
        if (!isBlackCell(r, c) && !newLitCells.has(`${r},${c}`)) {
          allLit = false;
          break;
        }
      }
      if (!allLit) break;
    }
    if (!allLit) return;

    // Check number constraints
    for (let r = 0; r < puzzleData.size; r++) {
      for (let c = 0; c < puzzleData.size; c++) {
        const val = puzzleData.grid[r][c];
        if (val >= 1 && val <= 4) {
          const adjacent = [[-1, 0], [1, 0], [0, -1], [0, 1]];
          let bulbCount = 0;
          adjacent.forEach(([dr, dc]) => {
            const nr = r + dr;
            const nc = c + dc;
            if (nr >= 0 && nr < puzzleData.size && nc >= 0 && nc < puzzleData.size) {
              if (bulbs.has(`${nr},${nc}`)) bulbCount++;
            }
          });
          if (bulbCount !== val) return;
        }
      }
    }

    // Won!
    const seconds = Math.floor((Date.now() - startTime) / 1000);
    const timeString = `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
    setDisplayTime(timeString);
    setWon(true);
    setGameOver(true);
    setMessage('Puzzle solved!');
    saveProgress({ completed: true, time: timeString, bulbCount: bulbs.size });
    
    // Send result to parent window (Webflow)
    window.parent.postMessage({
      type: 'GAME_COMPLETE',
      game: 'akari',
      data: {
        completed: true,
        time_seconds: seconds,
        bulbs_used: bulbs.size,
        difficulty: puzzleData.difficulty
      }
    }, '*');
  }, [bulbs, puzzleData, startTime, gameOver]);

  const cellKey = (r, c) => `${r},${c}`;

  const isBlackCell = (row, col) => puzzleData && puzzleData.grid[row][col] !== 0;

  const getBlackCellNumber = (row, col) => {
    if (!puzzleData) return null;
    const val = puzzleData.grid[row][col];
    if (val >= 1 && val <= 4) return val;
    return null;
  };

  const getNumberConstraintStatus = (row, col) => {
    const num = getBlackCellNumber(row, col);
    if (num === null) return null;
    
    const adjacent = [[-1, 0], [1, 0], [0, -1], [0, 1]];
    let bulbCount = 0;
    adjacent.forEach(([dr, dc]) => {
      const nr = row + dr;
      const nc = col + dc;
      if (nr >= 0 && nr < puzzleData.size && nc >= 0 && nc < puzzleData.size) {
        if (bulbs.has(cellKey(nr, nc))) bulbCount++;
      }
    });
    
    if (bulbCount === num) return 'satisfied';
    if (bulbCount > num) return 'exceeded';
    return 'unsatisfied';
  };

  const getCellFromPoint = (clientX, clientY) => {
    if (!gridRef.current || !puzzleData) return null;
    const rect = gridRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const cellSize = 40;
    const col = Math.floor(x / cellSize);
    const row = Math.floor(y / cellSize);
    if (row >= 0 && row < puzzleData.size && col >= 0 && col < puzzleData.size) {
      return { row, col };
    }
    return null;
  };

  const handlePointerDown = (e) => {
    if (gameOver) return;
    e.preventDefault();
    
    const cell = getCellFromPoint(e.clientX, e.clientY);
    if (!cell) return;
    if (isBlackCell(cell.row, cell.col)) return;

    const key = cellKey(cell.row, cell.col);
    const newBulbs = new Set(bulbs);

    if (newBulbs.has(key)) {
      newBulbs.delete(key);
    } else {
      newBulbs.add(key);
    }

    setBulbs(newBulbs);
  };

  const getCellStyle = (row, col) => {
    const key = cellKey(row, col);
    
    if (isBlackCell(row, col)) {
      return { backgroundColor: '#000' };
    }
    
    if (bulbs.has(key)) {
      if (conflicts.has(key)) {
        return { backgroundColor: '#ffcccc' };
      }
      return { backgroundColor: '#8ed88e' };
    }
    
    if (litCells.has(key)) {
      return { backgroundColor: '#8ed88e' };
    }
    
    return { backgroundColor: '#faf6e7' };
  };

  const generateShareText = () => {
    if (!puzzleData) return '';
    let text = `💡 Akari · ${formattedDate}\n`;
    text += `${puzzleData.size}×${puzzleData.size} ${puzzleData.difficulty}\n`;
    text += `⏱️ ${displayTime} | 💡 ${storedResult?.bulbCount || bulbs.size} bulbs\n`;
    text += won ? '✅ Solved!' : '❌ Incomplete';
    return text;
  };

  const handleCopy = () => {
    copyToClipboard(
      generateShareText(),
      () => {
        setMessage('Copied to clipboard!');
        setTimeout(() => setMessage(alreadyCompleted ? 'Already completed today!' : 'Puzzle solved!'), 2000);
      },
      () => setMessage('Could not copy')
    );
  };

  const resetPuzzle = () => {
    if (alreadyCompleted) return;
    setBulbs(new Set());
    setLitCells(new Set());
    setConflicts(new Set());
    setGameOver(false);
    setWon(false);
    setStartTime(Date.now());
    setDisplayTime('0:00');
    setMessage('Light up all cells!');
  };

  if (!puzzleData) {
    return <div style={{ padding: 16, textAlign: 'center', backgroundColor: '#faf6e7', color: '#000' }}>Loading...</div>;
  }

  // Styles
  const styles = {
    container: {
      padding: 16,
      maxWidth: 400,
      margin: '0 auto',
      userSelect: 'none',
      backgroundColor: '#faf6e7',
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif',
    },
    header: { textAlign: 'center', marginBottom: 16 },
    title: { fontSize: 28, fontWeight: 'bold', marginBottom: 4, color: '#000' },
    subtitle: { color: '#666', fontSize: 14 },
    message: { fontSize: 14, marginTop: 8, fontWeight: 600, color: '#ff0000' },
    statsRow: { display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 16, fontSize: 14 },
    statBadge: { backgroundColor: 'rgba(0,0,0,0.08)', color: '#000', padding: '6px 12px', borderRadius: 6, fontWeight: 500 },
    dateBadge: { backgroundColor: '#ff0000', color: '#fff', padding: '6px 12px', borderRadius: 6, fontWeight: 600 },
    grid: {
      display: 'grid',
      gap: 0,
      margin: '0 auto 16px',
      border: '2px solid #000',
      borderRadius: 4,
      overflow: 'hidden',
      width: 'fit-content',
      touchAction: 'none'
    },
    cell: {
      width: 40,
      height: 40,
      border: '1px solid rgba(0,0,0,0.15)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      fontSize: 18,
      cursor: 'pointer',
      transition: 'background-color 0.1s',
    },
    button: (primary) => ({
      padding: '10px 20px',
      borderRadius: 8,
      fontSize: 14,
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      backgroundColor: primary ? '#ff0000' : 'rgba(0,0,0,0.1)',
      color: primary ? '#fff' : '#000'
    }),
    banner: {
      backgroundColor: '#ff0000',
      borderRadius: 12,
      padding: 20,
      marginBottom: 16,
      textAlign: 'center'
    },
    countdownBox: {
      backgroundColor: 'rgba(0,0,0,0.05)',
      borderRadius: 8,
      padding: 12,
      marginBottom: 16,
      border: '1px solid rgba(0,0,0,0.1)',
      textAlign: 'center'
    },
    sharePreview: {
      padding: 12,
      backgroundColor: 'rgba(0,0,0,0.05)',
      borderRadius: 8,
      fontSize: 12,
      marginBottom: 12,
      whiteSpace: 'pre-wrap',
      textAlign: 'left',
      color: '#000',
      border: '1px solid rgba(0,0,0,0.1)'
    },
    rules: { marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(0,0,0,0.15)' },
    ruleText: { fontSize: 12, color: '#444', lineHeight: 1.6 },
    legend: { display: 'flex', justifyContent: 'center', gap: 16, marginTop: 12, fontSize: 12, color: '#666' },
    legendItem: { display: 'flex', alignItems: 'center', gap: 4 },
    legendDot: (color) => ({ width: 16, height: 16, backgroundColor: color, borderRadius: 3, border: '1px solid rgba(0,0,0,0.15)' })
  };

  return (
    <div ref={containerRef} style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>Akari</h2>
        <p style={styles.subtitle}>Light up all cells</p>
        <p style={styles.message}>{message}</p>
      </div>

      {/* Stats */}
      <div style={styles.statsRow}>
        <span style={styles.statBadge}>⏱️ {displayTime}</span>
        <span style={styles.statBadge}>{DIFFICULTIES[todaysDifficulty].name}</span>
        <span style={styles.dateBadge}>{formattedDate}</span>
      </div>

      {/* Game grid */}
      <div
        ref={gridRef}
        style={{ ...styles.grid, gridTemplateColumns: `repeat(${puzzleData.size}, 40px)` }}
        onPointerDown={handlePointerDown}
      >
        {puzzleData.grid.map((row, r) =>
          row.map((cell, c) => {
            const isBlack = isBlackCell(r, c);
            const num = getBlackCellNumber(r, c);
            const hasBulb = bulbs.has(cellKey(r, c));
            const constraintStatus = isBlack ? getNumberConstraintStatus(r, c) : null;
            const cellStyle = getCellStyle(r, c);
            
            return (
              <div key={cellKey(r, c)} style={{ ...styles.cell, ...cellStyle, cursor: isBlack ? 'default' : 'pointer' }}>
                {isBlack && num !== null && (
                  <span style={{
                    color: constraintStatus === 'satisfied' ? '#8ed88e' 
                         : constraintStatus === 'exceeded' ? '#ff0000' 
                         : '#fff'
                  }}>
                    {num}
                  </span>
                )}
                {hasBulb && <span style={{ fontSize: 20 }}>💡</span>}
              </div>
            );
          })
        )}
      </div>

      {/* Action buttons */}
      {!gameOver ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button onClick={resetPuzzle} style={styles.button(false)}>Reset</button>
        </div>
      ) : (
        <div style={{ textAlign: 'center' }}>
          <div style={styles.banner}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 'bold', color: '#fff' }}>
              {alreadyCompleted ? 'Already Completed!' : 'Puzzle Solved!'}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14, marginTop: 4 }}>
              Completed in {displayTime} with {storedResult?.bulbCount || bulbs.size} bulbs
            </div>
          </div>
          
          <div style={styles.countdownBox}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Next puzzle in</div>
            <div style={{ fontSize: 18, fontWeight: 'bold', color: '#000' }}>{countdown}</div>
          </div>
          
          <pre style={styles.sharePreview}>{generateShareText()}</pre>
          
          <button onClick={handleCopy} style={{ ...styles.button(false), backgroundColor: '#000', color: '#fff' }}>
            📋 Share Result
          </button>
        </div>
      )}

      {/* Rules */}
      <div style={styles.rules}>
        <p style={{ fontSize: 12, color: '#666', textAlign: 'center', marginBottom: 8 }}>How to play:</p>
        <div style={styles.ruleText}>
          <p>• Tap cells to place 💡 bulbs</p>
          <p>• Bulbs light up all cells in their row/column until blocked</p>
          <p>• Numbers show exactly how many bulbs must touch that cell</p>
          <p>• Bulbs cannot shine on each other (no conflicts!)</p>
        </div>
        <div style={styles.legend}>
          <span style={styles.legendItem}><span style={styles.legendDot('#8ed88e')} /> Bulb</span>
          <span style={styles.legendItem}><span style={styles.legendDot('#8ed88e')} /> Lit</span>
          <span style={styles.legendItem}><span style={styles.legendDot('#ffcccc')} /> Conflict</span>
        </div>
      </div>
    </div>
  );
};

export default Akari;
